import type { DomainGuide } from '@/components/guide/guide-types';

import th from './th.json';
import en from './en.json';
import zh from './zh.json';
import vi from './vi.json';
import km from './km.json';
import lo from './lo.json';
import my from './my.json';

/**
 * Help catalog for the `risk` domain — the six tabs of `/risk`.
 *
 * One page, three backends: alerts / multi-accounts / online come from
 * svc-monitor, exposure / anomalies from svc-bet, self-exclusion from
 * svc-member. That split is why the tabs behave so differently, and why two
 * of them carry a status banner:
 *
 *  - `online` is structurally empty. The presence row is written by
 *    `POST /online/heartbeat`, which the gateway publishes to the `bo`
 *    audience only (upstreams.ts) while svc-monitor rejects any caller whose
 *    token is not a player's (routes/online.ts) — so no caller can reach it,
 *    and nothing in the player app tries.
 *  - `multiAccounts` is computed from that same empty table
 *    (repo/multi-account.ts `refreshClustersFromOnline`), so Refresh can only
 *    ever find nothing.
 *
 * `houseNet` on exposure is `turnover − payout` — the INVERSE of the member
 * list's `netDifference`. Both sign legends spell out who gained, because the
 * same green bar means opposite things on the two screens.
 */
export const riskGuide: DomainGuide = {
  domain: 'risk',
  pages: [
    {
      id: 'risk.alerts',
      route: '/risk → Alerts',
      permission: 'monitor:read',
      status: 'live',
      fields: [
        { key: 'createdAt', kind: 'date' },
        { key: 'severity', kind: 'status' },
        { key: 'kind', kind: 'status' },
        { key: 'subjectKind', kind: 'text' },
        { key: 'subjectId', kind: 'text' },
        { key: 'summary', kind: 'text' },
        { key: 'acknowledged', kind: 'status' },
      ],
      cautionCount: 5,
      warningCount: 0,
    },
    {
      id: 'risk.multiAccounts',
      route: '/risk → Multi-accounts',
      permission: 'monitor:read',
      status: 'partially-wired',
      fields: [
        { key: 'clusterKey', kind: 'text' },
        { key: 'reason', kind: 'status' },
        { key: 'memberCount', kind: 'count' },
        { key: 'lastSeen', kind: 'date' },
        { key: 'resolved', kind: 'status' },
      ],
      cautionCount: 5,
      warningCount: 0,
    },
    {
      id: 'risk.online',
      route: '/risk → Online',
      permission: 'monitor:read',
      status: 'partially-wired',
      fields: [
        { key: 'username', kind: 'text' },
        { key: 'ipAddress', kind: 'text' },
        { key: 'device', kind: 'text' },
        { key: 'connectedAt', kind: 'date' },
        { key: 'lastActivityAt', kind: 'date' },
      ],
      cautionCount: 4,
      warningCount: 0,
    },
    {
      id: 'risk.exposure',
      route: '/risk → Exposure',
      permission: 'monitor:read',
      status: 'live',
      fields: [
        { key: 'provider', kind: 'text' },
        { key: 'gameType', kind: 'text' },
        { key: 'currency', kind: 'text' },
        { key: 'turnover', kind: 'money-unsigned' },
        { key: 'payout', kind: 'money-unsigned' },
        {
          key: 'houseNet',
          kind: 'money-signed',
          formula: 'houseNet = turnover − payout (house perspective)',
        },
      ],
      cautionCount: 4,
      warningCount: 0,
    },
    {
      id: 'risk.anomalies',
      route: '/risk → Win-rate anomalies',
      permission: 'monitor:read',
      status: 'live',
      fields: [
        { key: 'member', kind: 'text' },
        { key: 'currency', kind: 'text' },
        { key: 'rtp', kind: 'basis-points', formula: 'RTP = payout ÷ turnover' },
        { key: 'turnover', kind: 'money-unsigned' },
        { key: 'payout', kind: 'money-unsigned' },
        { key: 'betCount', kind: 'count' },
      ],
      cautionCount: 5,
      warningCount: 0,
    },
    {
      /**
       * The only tab on this page that is not about players. It is a view of
       * COLLEAGUES, which is why it sits behind its own permission on the
       * supervisor rung rather than `monitor:read` like its neighbours, and why
       * the copy spells out what presence does NOT observe (ADR-0001 keeps
       * remote staff in this same back office, so the answer has to be visible
       * to the people being listed).
       */
      id: 'risk.staffOnline',
      route: '/risk → Staff online',
      permission: 'staff_presence:read',
      status: 'live',
      fields: [
        { key: 'username', kind: 'text' },
        { key: 'role', kind: 'text' },
        { key: 'ipAddress', kind: 'text' },
        { key: 'currentPage', kind: 'text' },
        { key: 'lastActivity', kind: 'date' },
        { key: 'presence', kind: 'status' },
        // The lower half of the same tab: six per-staff ticket figures
        // for a Bangkok date range. They document the SECOND table on this
        // screen rather than a page of their own, because a supervisor reads
        // the board and the numbers as one answer to "who is working, and how
        // is the queue moving".
        //
        // The two averages are `text` and not `date`: a date field is an
        // instant, and these are spans rendered through `formatDurationShort`
        // with units. There is no duration kind, and inventing one here would
        // change the shared legend for every domain.
        { key: 'ticketsOpened', kind: 'count' },
        { key: 'ticketsAccepted', kind: 'count' },
        { key: 'ticketsCompleted', kind: 'count' },
        { key: 'ticketsRejected', kind: 'count' },
        { key: 'ticketsAvgAccept', kind: 'text' },
        { key: 'ticketsAvgComplete', kind: 'text' },
      ],
      cautionCount: 7,
      warningCount: 0,
    },
    {
      id: 'risk.selfExclusion',
      route: '/risk → Self-exclusion',
      permission: 'member:update',
      status: 'live',
      fields: [
        { key: 'kind', kind: 'status' },
        { key: 'setByKind', kind: 'text' },
        { key: 'startsAt', kind: 'date' },
        { key: 'expiresAt', kind: 'date' },
        { key: 'reason', kind: 'text' },
        { key: 'status', kind: 'status' },
      ],
      cautionCount: 6,
      warningCount: 1,
    },
  ],
  copy: { th, en, zh, vi, km, lo, my },
};
